import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { User, UserCircle, Mail, Phone, MapPin, Calendar, Book, Award } from "lucide-react";
import { adminDashboard } from '../../services/api';

// Interface definitions
interface UserDetails {
  id: number;
  fullName: string;
  email: string;
  photo: string | null;
  phone: string | null;
  createdAt: string;
  stats: {
    formationsCompleted: number;
    formationsInProgress: number;
    chaptersCompleted: number;
  };
}

interface Formation {
  id: number;
  name: string;
  category: string;
  totalChapters: number;
  progress: number;
  chaptersCompleted: number;
}

// Avatar Component
const UserAvatar: React.FC<{ 
  photo: string | null; 
  fullName: string; 
  size?: 'sm' | 'md' | 'lg' 
}> = ({ photo, fullName, size = 'lg' }) => {
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-16 w-16 text-lg',
    lg: 'h-32 w-32 text-2xl'
  };

  const getUserPhotoUrl = (photoPath: string | null): string | undefined => {
    if (!photoPath) return undefined; // ✅ Return undefined instead of null
    if (photoPath.startsWith('http')) return photoPath;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${photoPath}`;
  };

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      // Get first letter of first name and first letter of last name
      return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    } else if (words.length === 1) {
      // If only one word, get first two letters
      return words[0].substring(0, 2).toUpperCase();
    }
    return 'U'; // Default fallback
  };

  if (!photo || imageError) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-[#00088E] to-[#0066CC] flex items-center justify-center text-white font-bold mb-4 shadow-lg`}>
        {getInitials(fullName)}
      </div>
    );
  }

  return (
    <img 
      src={getUserPhotoUrl(photo)} 
      alt={fullName}
      className={`${sizeClasses[size]} rounded-full object-cover mb-4 shadow-lg border-4 border-white`}
      onError={() => setImageError(true)}
    />
  );
};

export default function AdminUserDetails() {
  const { userId, id } = useParams<{ userId?: string; id?: string }>();
  const actualUserId = userId || id;
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [formations, setFormations] = useState<Formation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les données utilisateur
  useEffect(() => {
    const fetchUserData = async () => {
      if (!actualUserId) {
        setError("ID utilisateur manquant");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Charger les détails de l'utilisateur
        const detailsResponse = await adminDashboard.getUserDetails(Number(actualUserId));
        if (detailsResponse.data.success) {
          setUserDetails(detailsResponse.data.data);
        }

        // Charger les formations de l'utilisateur
        const formationsResponse = await adminDashboard.getUserFormations(Number(actualUserId));
        if (formationsResponse.data.success) {
          setFormations(formationsResponse.data.formations);
        }

      } catch (err) {
        console.error('Erreur lors du chargement des données utilisateur:', err);
        setError('Erreur lors du chargement des données utilisateur');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [actualUserId]);

  // Fonction pour formater la date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      };

      return date.toLocaleDateString('fr-FR', options);
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return dateString;
    }
  };

  // Function to determine progress color
  const getProgressColor = (progress: number): string => {
    if (progress < 30) return "bg-red-500";
    if (progress < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <div className="text-lg">Chargement...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-lg text-red-500 mb-4">{error || 'Utilisateur introuvable'}</div>
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex items-center mb-8">
          <button 
            onClick={() => window.history.back()} 
            className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-3xl font-bold">Profil utilisateur</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile information card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-col items-center mb-6">
                <UserAvatar 
                  photo={userDetails.photo} 
                  fullName={userDetails.fullName} 
                  size="lg" 
                />
                <h2 className="text-2xl font-bold text-center">{userDetails.fullName}</h2>
                <p className="text-gray-500 text-sm mt-1">ID: {userDetails.id}</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-gray-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-500">Adresse e-mail</p>
                    <p className="break-words">{userDetails.email}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="w-5 h-5 text-gray-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Téléphone</p>
                    <p>{userDetails.phone || 'Non renseigné'}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-gray-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Membre depuis</p>
                    <p>{formatDate(userDetails.createdAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enrolled courses and progress */}
          <div className="lg:col-span-2">
            {/* Statistics Section */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Formations complétées</p>
                    <h3 className="text-2xl font-bold text-green-600">
                      {userDetails.stats.formationsCompleted}
                    </h3>
                  </div>
                  <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Award className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Formations en cours</p>
                    <h3 className="text-2xl font-bold text-blue-600">
                      {userDetails.stats.formationsInProgress}
                    </h3>
                  </div>
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Book className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Chapitres terminés</p>
                    <h3 className="text-2xl font-bold text-purple-600">
                      {userDetails.stats.chaptersCompleted}
                    </h3>
                  </div>
                  <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Formations</h2>
                <div className="bg-[#00088E]/10 text-[#00088E] font-medium px-3 py-1 rounded-full text-sm">
                  {formations.length} formation{formations.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="space-y-6">
                {formations.map((formation) => (
                  <div key={formation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-lg">{formation.name}</h3>
                        <p className="text-sm text-gray-500">{formation.category}</p>
                      </div>
                    </div>

                    <div className="mb-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progression: {formation.progress}%</span>
                        <span>
                          {formation.chaptersCompleted}/{formation.totalChapters} chapitres
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className={`h-2.5 rounded-full transition-all duration-300 ${getProgressColor(formation.progress)}`}
                          style={{ width: `${formation.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center mt-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Book className="w-4 h-4 mr-1" />
                        <span>{formation.totalChapters} chapitre{formation.totalChapters !== 1 ? 's' : ''}</span>
                      </div>
                      {formation.progress === 100 ? (
                        <div className="flex items-center text-green-600 text-sm">
                          <Award className="w-4 h-4 mr-1" />
                          <span>Formation complétée</span>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">
                          En cours
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {formations.length === 0 && (
                <div className="text-center py-12">
                  <div className="bg-gray-100 inline-flex rounded-full p-4 mb-4">
                    <Book className="h-8 w-8 text-gray-500" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Aucune formation</h3>
                  <p className="text-gray-500 mb-4 max-w-md mx-auto">
                    Cet utilisateur n'est inscrit à aucune formation pour le moment.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}